import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { JsonValidator } from '../utils/JsonValidator'
import { JsonFormatter } from '../utils/JsonFormatter'
import { LocalStorageService } from '../utils/LocalStorageService'

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export interface JsonEditorState {
  jsonContent: string
  validationResult: ValidationResult
  isFormatted: boolean
  showCopied: boolean
  isValidationPanelCollapsed: boolean
  isDarkMode: boolean
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  saveError?: string
  isTreeVisualizerVisible: boolean
}

export const DEFAULT_SAMPLE_JSON = `[
  {
    "name": "Woof",
    "species": "dog",
    "foods": {
      "favorite": "kibble",
      "treats": "bones"
    }
  },
  {
    "name": "Fluffy",
    "species": "cat",
    "foods": {
      "favorite": "fish",
      "treats": "catnip"
    }
  }
]`

export const useJsonEditor = () => {
  const [state, setState] = useState<JsonEditorState>(() => {
    let initialContent = DEFAULT_SAMPLE_JSON
    let isDark = true

    if (LocalStorageService.isAvailable()) {
      const result = LocalStorageService.loadJsonContent()
      if (result.success && result.hasStoredData) {
        initialContent = result.data ?? ''
      }
      const themeResult = LocalStorageService.loadThemePreference()
      if (themeResult.success && themeResult.isDarkMode !== undefined) {
        isDark = themeResult.isDarkMode
      }
    }

    let initialValidation = { isValid: true, errors: [] as string[], warnings: [] as string[] }
    if (initialContent.trim()) {
      try {
        initialValidation = JsonValidator.validate(initialContent)
      } catch {
        initialValidation = { isValid: false, errors: ['Validation failed'], warnings: [] }
      }
    }

    return {
      jsonContent: initialContent,
      validationResult: initialValidation,
      isFormatted: false,
      showCopied: false,
      isValidationPanelCollapsed: true,
      isDarkMode: isDark,
      saveStatus: 'idle',
      isTreeVisualizerVisible: true
    }
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const isFirstRender = useRef<boolean>(true)

  // Sample JSON for demonstration
  const sampleJson = DEFAULT_SAMPLE_JSON

  // Debounced validation to improve performance
  const debouncedValidation = useCallback(
    debounce((content: string) => {
      if (content.trim()) {
        try {
          const result = JsonValidator.validate(content)
          setState(prev => ({ ...prev, validationResult: result }))
        } catch (error) {
          setState(prev => ({ ...prev, validationResult: { isValid: false, errors: ['Validation failed'], warnings: [] } }))
        }
      } else {
        setState(prev => ({
          ...prev,
          validationResult: { isValid: true, errors: [], warnings: [] }
        }))
      }
    }, 300),
    [JsonValidator]
  )

  // Check localStorage availability on mount
  useEffect(() => {
    if (!LocalStorageService.isAvailable()) {
      setState(prev => ({ 
        ...prev, 
        saveStatus: 'error',
        saveError: 'Local storage is not available. Your content will not be saved automatically.'
      }))
    }
  }, [])

  // Auto-save content when it changes (debounced)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (LocalStorageService.isAvailable()) {
      setState(prev => ({ ...prev, saveStatus: 'saving' }))
      
      const timeoutId = setTimeout(() => {
        const saveResult = LocalStorageService.saveJsonContent(state.jsonContent)
        
        if (saveResult.success) {
          setState(prev => ({ ...prev, saveStatus: 'saved' }))
          // Clear save status after 2 seconds
          setTimeout(() => {
            setState(prev => ({ ...prev, saveStatus: 'idle' }))
          }, 2000)
        } else {
          setState(prev => ({ 
            ...prev, 
            saveStatus: 'error',
            saveError: saveResult.error
          }))
        }
      }, 500) // 500ms debounce

      return () => clearTimeout(timeoutId)
    } else {
      // Show error if localStorage is not available
      setState(prev => ({ 
        ...prev, 
        saveStatus: 'error',
        saveError: 'Local storage is not available. Your content will not be saved automatically.'
      }))
    }
  }, [state.jsonContent])

  useEffect(() => {
    // Validate JSON whenever content changes
    debouncedValidation(state.jsonContent)
  }, [state.jsonContent, debouncedValidation])

  const updateJsonContent = useCallback((content: string) => {
    setState(prev => ({ 
      ...prev, 
      jsonContent: content,
      isFormatted: false 
    }))
  }, [])

  const handleFormat = useCallback(() => {
    if (state.jsonContent.trim()) {
      try {
        const formatted = JsonFormatter.format(state.jsonContent)
        setState(prev => ({ 
          ...prev, 
          jsonContent: formatted,
          isFormatted: true 
        }))
      } catch (error) {
        console.error('Formatting failed:', error)
      }
    }
  }, [state.jsonContent])

  const handleMinify = useCallback(() => {
    if (state.jsonContent.trim()) {
      try {
        const minified = JsonFormatter.minify(state.jsonContent)
        setState(prev => ({ 
          ...prev, 
          jsonContent: minified,
        isFormatted: false 
        }))
      } catch (error) {
        console.error('Minification failed:', error)
      }
    }
  }, [state.jsonContent])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(state.jsonContent)
      setState(prev => ({ ...prev, showCopied: true }))
      setTimeout(() => {
        setState(prev => ({ ...prev, showCopied: false }))
      }, 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }, [state.jsonContent])

  const handleDownload = useCallback(() => {
    const blob = new Blob([state.jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'data.json'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [state.jsonContent])

  const handleUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        const content = e.target?.result as string
        setState(prev => ({ 
          ...prev, 
          jsonContent: content,
          isFormatted: false 
        }))
      }
      
      reader.onerror = () => {
        console.error('Failed to read file')
      }
      
      reader.readAsText(file)
    }
  }, [])

  const handleClear = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      jsonContent: '',
      isFormatted: false
    }))
    
    // Explicitly save empty content to storage so refresh preserves empty state
    if (LocalStorageService.isAvailable()) {
      LocalStorageService.saveJsonContent('')
    }
  }, [])

  const handleLoadSample = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      jsonContent: sampleJson,
      isFormatted: false 
    }))
  }, [sampleJson])

  const handlePaste = useCallback((content: string) => {
    setState(prev => ({ 
      ...prev, 
      jsonContent: content,
      isFormatted: false 
    }))
  }, [])

  const toggleValidationPanel = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isValidationPanelCollapsed: !prev.isValidationPanelCollapsed 
    }))
  }, [])

  const expandValidationPanel = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isValidationPanelCollapsed: false 
    }))
  }, [])

  const toggleTheme = useCallback(() => {
    setState(prev => {
      const newDarkMode = !prev.isDarkMode
      
      // Save theme preference to localStorage
      if (LocalStorageService.isAvailable()) {
        LocalStorageService.saveThemePreference(newDarkMode)
      }
      
      return { 
        ...prev, 
        isDarkMode: newDarkMode 
      }
    })
  }, [])

  const toggleTreeVisualizer = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isTreeVisualizerVisible: !prev.isTreeVisualizerVisible 
    }))
  }, [])

  const manualSave = useCallback(() => {
    if (LocalStorageService.isAvailable() && state.jsonContent.trim()) {
      setState(prev => ({ ...prev, saveStatus: 'saving' }))
      const saveResult = LocalStorageService.saveJsonContent(state.jsonContent)
      if (saveResult.success) {
        setState(prev => ({ ...prev, saveStatus: 'saved' }))
        setTimeout(() => {
          setState(prev => ({ ...prev, saveStatus: 'idle' }))
        }, 2000)
      } else {
        setState(prev => ({ 
          ...prev, 
          saveStatus: 'error',
          saveError: saveResult.error
        }))
      }
    }
  }, [state.jsonContent])

  const hasIssues = useMemo(() => 
    state.validationResult.errors.length > 0 || state.validationResult.warnings.length > 0,
    [state.validationResult]
  )

  return {
    state,
    fileInputRef,
    updateJsonContent,
    handleFormat,
    handleMinify,
    handleCopy,
    handleDownload,
    handleUpload,
    handleClear,
    handleLoadSample,
    handlePaste,
    toggleValidationPanel,
    expandValidationPanel,
    toggleTheme,
    toggleTreeVisualizer,
    manualSave,
    hasIssues
  }
}

// Utility function for debouncing
/* eslint-disable no-unused-vars */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}
/* eslint-enable no-unused-vars */
